import { Layer, Stage } from 'react-konva';
// import ColumnHeader from './columnHeader/ColumnHeader';
import { Size } from '../../../core/types/ui/size';
import { WindowSizes } from '../../../windows/constants/windowSizes';
import ColumnHeader from './columnHeader/ColumnHeader';
import CellsPane from './cellsPane/CellsPane';
import RowHeader from './rowHeader/RowHeader';
import ColumnFilter from './columnFilter/ColumnFilter';
import { useAppSelector } from '../../../core/hooks/storeHooks';
import {
  ReturnTypeofSheetSectionsSelector,
  selectSheetSections,
} from '../../selectors/sheetUiSelector';
import ColumnFilterResult from './columnFilterResult/ColumnFilterResult';
import { SheetDefaults } from '../../constants/sheetDefaults';
import { WindowGeometry } from '../../../windows/types';
import FrozenLinesKonva from './freeze/FrozenLinesKonva';
import SheetViews from '../views/Views';
import useCellSelection from '../../hooks/useCellSelection';
import useKeys from '../../hooks/useKeys';
import { useRef } from 'react';
import ActiveCell from '../views/selections/ActiveCell';
import useSheetTheme from '../../hooks/useSheetTheme';
import ColumnGroups from './groups/ColumnGroups';
import MoveableFrozenLines from './freeze/MoveableFrozenLines';
import Resizers from './resizers/Resizers';
import RowGroups from './groups/RowGroups';
import { Box } from '@chakra-ui/react';
import RowScrollbar from './scrollbars/RowScrollbar';
import ColScrollbar from './scrollbars/ColScrollbar';
import useScrollbar from '../../hooks/useScrollbar';
import FrozenSizeWarning from './freeze/FrozenSizeWarning';
import useGridOperations from '../../hooks/useGridOperations';
import GridContextMenu from './contextMenu/GridContextMenu';
import { IPosition2d } from '../../../core/types/ui/position';
import useFilterDropdown from '../../hooks/useFilterDropdown';
import FilterDropdown from './filterDropdown/FilterDropdown';
import useKonvaCursor from '../../../core/hooks/useKonvaCursor';
// import ColumnFilter from './columnFilter/ColumnFilter';

interface GridProps extends Size, IPosition2d {
  sheetId: string;
}
const Grid: React.FC<GridProps> = ({
  sheetId,
  width: windowWidth,
  height: windowHeight,
  x: windowX,
  y: windowY,
}) => {
  // #region hooks and refs
  // const windowGeometry = {
  //   x: windowX,
  //   y: windowY,
  //   width: windowWidth,
  //   height: windowHeight,
  // };

  const sheetUiProps = useAppSelector((state) =>
    selectSheetSections(sheetId)(state)
  );
  const sheetRef = useRef<HTMLDivElement>(null);
  const themeProps = useSheetTheme();

  const { onMouseEnter, onMouseLeave } = useKonvaCursor();

  const focusSheet = () => {
    sheetRef.current?.focus({
      preventScroll: true,
    });
  };

  const { openContextMenu, performSheetOperation, ...contextMenuProps } =
    useGridOperations(sheetId, focusSheet);

  const filterDropdownProps = useFilterDropdown(focusSheet);

  const { x, y, width, height } = getGridDimensions(
    windowWidth,
    windowHeight,
    sheetUiProps
  );

  const onKeyDown = useKeys(
    sheetId,
    windowX,
    windowY,
    windowWidth,
    windowHeight,
    sheetUiProps,
    width,
    themeProps.isRtl,
    openContextMenu
  );

  const {
    onCellMouseDown,
    onCellMouseHover,
    handleSelectionInteraction,
    onActiveHeaderMouseDown,
  } = useCellSelection(
    sheetId,
    sheetUiProps.freezedColsSum,
    sheetUiProps.freezedRowsSum
  );

  const { handleWheelScroll, ...scrollbarProps } = useScrollbar(
    sheetId,
    windowWidth,
    windowHeight,
    sheetUiProps.maxColIndex,
    sheetUiProps.maxRowIndex
  );

  // #endregion

  // #region views
  const frozenLines = () => (
    <FrozenLinesKonva
      x={0} // x
      y={0} // y
      width={width}
      height={height}
      {...sheetUiProps}
      {...themeProps}
    />
  );

  const moveableFrozenLines = () => (
    <MoveableFrozenLines
      sheetId={sheetId}
      x={0} // x
      y={0} // y
      width={width}
      height={height}
      windowWidth={windowWidth}
      windowHeight={windowHeight}
      {...sheetUiProps}
      {...themeProps}
    />
  );

  const resizersView = () => (
    <Resizers
      {...sheetUiProps}
      {...themeProps}
      sheetId={sheetId}
      x={0} // x
      y={0} // y
      width={width}
      height={height}
      gridWidth={width}
    />
  );

  const sheetViews = () => (
    <SheetViews
      sheetId={sheetId}
      windowWidth={windowWidth}
      windowHeight={windowHeight}
      gridWidth={width}
      gridHeight={height}
      {...sheetUiProps}
      {...themeProps}
      handleSelectionInteraction={handleSelectionInteraction}
    />
  );

  const rowScrollbarView = () => (
    <RowScrollbar
      {...sheetUiProps}
      {...themeProps}
      {...scrollbarProps}
      sheetId={sheetId}
      gridHeight={height}
      windowWidth={windowWidth}
      windowHeight={windowHeight}
      // windowY={windowY}
    />
  );

  const colScrollbarView = () => (
    <ColScrollbar
      {...sheetUiProps}
      {...themeProps}
      {...scrollbarProps}
      sheetId={sheetId}
      gridWidth={width}
      windowWidth={windowWidth}
      windowHeight={windowHeight}
      windowX={windowX}
    />
  );

  const renderActiveCell = () => (
    <ActiveCell {...sheetUiProps} {...themeProps} stageWidth={width} />
  );

  const frozenSizeWarningView = () => {
    const { rowHeaderWidth, colHeaderHeight, colFilterHeight } = sheetUiProps;
    const cellAreaWidth = width - rowHeaderWidth;
    const cellAreaHeight = height - colHeaderHeight - colFilterHeight;
    return (
      (sheetUiProps.freezedColsSum >= cellAreaWidth ||
        sheetUiProps.freezedRowsSum >= cellAreaHeight) && (
        <FrozenSizeWarning
          stroke={themeProps.stroke}
          gridHeight={height}
          gridWidth={width}
        />
      )
    );
  };

  const contextMenuView = () =>
    contextMenuProps.menuState.state === 'open' && (
      <GridContextMenu
        sheetId={sheetId}
        textColor={themeProps.textColor}
        fill={themeProps.fill}
        {...contextMenuProps}
        {...sheetUiProps}
        // performGridOperation={performGridOperation}
        // performCellOperation={performCellOperation}
        // {...themeProps}
      />
    );

  const filterDropdown = () =>
    filterDropdownProps.filterMenuState.state === 'open' && (
      <FilterDropdown
        sheetId={sheetId}
        textColor={themeProps.textColor}
        fill={themeProps.fill}
        isDarkTheme={themeProps.isDarkTheme}
        stroke={themeProps.stroke}
        {...filterDropdownProps}
      />
    );

  const gridView = (
    <Box
      position="relative"
      top={`${y}px`}
      width={`${width}px`}
      height={`${height}px`}
      display="inline-block"
      onContextMenu={openContextMenu}
    >
      <Stage x={0} y={0} width={width} height={height} id={sheetId}>
        <Layer>
          <CellsPane
            sheetId={sheetId}
            {...sheetUiProps}
            {...themeProps}
            gridWidth={width}
            onCellMouseDown={onCellMouseDown}
            onCellMouseHover={onCellMouseHover}
            performSheetOperation={performSheetOperation}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            // performCellOperation={performCellOperation}
          />
          <ColumnFilter
            sheetId={sheetId}
            gridWidth={width}
            {...sheetUiProps}
            {...themeProps}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            openFilterDropdown={filterDropdownProps.openFilterDropdown}
            // closeFilterDropdown={filterDropdownProps.closeFilterDropdown}
          />
          <ColumnHeader
            sheetId={sheetId}
            gridWidth={width}
            {...sheetUiProps}
            {...themeProps}
            onCellMouseDown={onCellMouseDown}
            onActiveHeaderMouseDown={onActiveHeaderMouseDown}
          />

          <RowHeader
            sheetId={sheetId}
            {...sheetUiProps}
            {...themeProps}
            gridWidth={width}
            onCellMouseDown={onCellMouseDown}
            onActiveHeaderMouseDown={onActiveHeaderMouseDown}
          />
          {frozenLines()}
          {renderActiveCell()}
        </Layer>
      </Stage>
      {moveableFrozenLines()}
      {sheetViews()}
      {resizersView()}
      <ColumnFilterResult sheetId={sheetId} {...sheetUiProps} {...themeProps} />
      {rowScrollbarView()}
      {colScrollbarView()}
      {frozenSizeWarningView()}
    </Box>
  );
  // #endregion

  return (
    <Box
      tabIndex={-1}
      onMouseDown={focusSheet}
      ref={sheetRef}
      onWheel={handleWheelScroll}
      onKeyDown={onKeyDown}
    >
      <ColumnGroups
        x={x}
        y={y}
        sheetId={sheetId}
        gridWidth={width}
        {...sheetUiProps}
        {...themeProps}
        // performGridOperation={performGridOperation}
        openContextMenu={openContextMenu}
      />
      <RowGroups
        y={y}
        sheetId={sheetId}
        gridHeight={height}
        {...sheetUiProps}
        {...themeProps}
        openContextMenu={openContextMenu}
        // performGridOperation={performGridOperation}
      />
      {gridView}
      {contextMenuView()}
      {filterDropdown()}
    </Box>
  );
};

// #region helper
const getGridDimensions = (
  windowWidth: number,
  windowHeight: number,
  props: ReturnTypeofSheetSectionsSelector
  // isRtl: boolean
): WindowGeometry => {
  const y = props.formulaBarHeight; //+ props.groupBarHeight;
  const height =
    windowHeight -
    WindowSizes.controlBarHeight -
    SheetDefaults.scrollBarHeight -
    SheetDefaults.statusBarHeight -
    props.formulaBarHeight -
    props.groupBarHeight;

  const right = SheetDefaults.scrollBarWidth + props.shortcutBarWidth;
  const width =
    windowWidth -
    right -
    // SheetDefaults.scrollBarWidth -
    // props.shortcutBarWidth -
    props.rowGroupBarWidth;

  const left = props.rowGroupBarWidth;

  // const x = isRtl ? right : props.rowGroupBarWidth;

  // console.log('master x', x);
  // windowWidth - props.rowGroupBarWidth - freezedColsSum

  // const style: CSSProperties = {
  //   width: `${width}px`,
  //   height: `${height}px`,
  //   top: `${y}px`,
  // };

  // if (isRtl) {
  //   style.right = `${left}px`;
  // } else {
  //   style.left = `${left}px`;
  // }

  return {
    x: left, // 0
    y,
    width,
    height,
    // style,
  };
};
// #endregion

export default Grid;
